<template>
    <ElDialog class="modal modal-subscription-cancel" :visible.sync="showModal" :width="modalWidth"
        :close-on-click-modal="false" destroy-on-close>
        <h2 class="modal__title">
            Отмена подписки
        </h2>

        <div class="modal__body">
            <p class="modal__text">
                {{ modalSubtitle }}
            </p>

            <ElForm v-if="currentStep === steps[0] || currentStep === steps[1]" class="modal-subscription-cancel__form form"
                ref="propertyFormRef" :model="formData">
                <ElFormItem v-show="currentStep === steps[0]" class="form__item" :error="formData.phone.errorText">
                    <ElInput id="form-phone-text" v-model="formData.phone.text"
                        v-mask="{ regex: '^\\+7 \\([0-9]${3}\\) [0-9]${3} [0-9]${2}-[0-9]${2}' }" placeholder="Телефон"
                        @input="formData.phone.errorText = ''">
                        <template #prefix>
                            Телефон
                        </template>
                    </ElInput>
                </ElFormItem>

                <ElFormItem v-show="currentStep === steps[1]" class="form__item" :error="formData.code.errorText">
                    <ElInput id="form-code-text" v-model="formData.code.text" placeholder="Код из смс" @input="formData.code.errorText = ''">
                        <template #prefix>
                            Код из смс
                        </template>
                    </ElInput>
                </ElFormItem>

                <div v-if="codeWasSent" class="modal-subscription-cancel__code-repeat">
                    <p v-if="codeRepeatTimeout != 0" class="modal-subscription-cancel__code-repeat-comment">Повторный код
                        можно запросить
                        <span class="modal-subscription-cancel__code-repeat-comment--accent">
                            через {{ codeRepeatTimeout }} сек.
                        </span>
                    </p>
                    <BaseButton class="modal-subscription-cancel__code-repeat-button-send" text="Отправить повторно" is-text
                        size="small" :is-disabled="codeRepeatTimeout > 0" @click.native="onCodeRepeatRequest" />
                </div>
            </ElForm>

            <ElForm v-if="currentStep === steps[2]" class="modal-subscription-cancel__radio form">
                <ElFormItem class="form__item" :error="formData.cancelReason.errorText">
                    <ElRadioGroup id="cancel-reason" class="modal-subscription-cancel__radio-group" v-model="formData.cancelReason.value">
                        <ElRadio v-input-id="`cancel-reason-${reason.value}`" v-for="reason in cancelReasons" :key="reason.value" :label="reason.label">
                            {{ reason.label }}
                        </ElRadio>
                    </ElRadioGroup>
                </ElFormItem>
            </ElForm>
        </div>

        <div class="modal__submit-buttons">
            <BaseButton v-if="currentStep !== steps[steps.length - 1]" text="Отмена" is-text theme="muted" size="large"
                @click.native="closeModal" />
            <BaseButton :text="submitButtonText" size="large" :is-disabled="submitIsDisabled" @click.native="onSubmit" />
        </div>
    </ElDialog>
</template>

<script>
import modal from '@/mixins/modal';
import BaseButton from '@/components/ui/BaseButton';
import { numbersLengthFromString } from '@/utils/string-helpers';
import { millisToMinutesAndSeconds } from '@/utils/date-helpers';
import { SUBSCRIPTION_CANCEL_REASONS, CODE_REPEAT_MAX_TIMEOUT, PHONE_LENGTH } from '@/consts';
import { getCancelSmsCodeByPhoneNumber, submitCancelSmsCode, submitSubscriptionCancel } from '@/api/routes/subscription-cancel';

export default {
    name: 'ModalSubscriptionCancel',
    components: {
        BaseButton
    },
    mixins: [modal],
    data() {
        return {
            steps: ['enter-phone', 'code-sent', 'questionnaire', 'final'],
            currentStep: 'enter-phone',
            formData: {
                phone: {
                    text: '',
                    errors: {
                        no_subscriptions: 'На указанный номер нет активных подписок. Попробуйте еще раз.'
                    },
                    errorText: ''
                },
                code: {
                    text: '',
                    errors: {
                        invalid_code: 'Указан неверный код, попробуйте еще раз.'
                    },
                    errorText: ''
                },
                cancelReason: {
                    value: SUBSCRIPTION_CANCEL_REASONS[0].label,
                    errors: {
                        cancel_error: 'При отмене подписки произошла ошибка. Попробуйте снова'
                    },
                    errorText: ''
                }
            },
            codeWasSent: false,
            codeRepeatTimeout: CODE_REPEAT_MAX_TIMEOUT,
            codeRepeatIntervalId: null,
        }
    },
    methods: {
        async onSubmit() {
            const codeField = this.formData.code;
            const phoneField = this.formData.phone;

            switch (this.steps.indexOf(this.currentStep)) {
                case 0:
                    const successPhoneSubmit = await this.processPhoneSubmit();

                    if (successPhoneSubmit) {
                        this.currentStep = this.steps[1];
                        this.codeWasSent = true;
                    } else {
                        phoneField.errorText = phoneField.errors.no_subscriptions;
                    }

                    break;
                case 1:
                    const successCodeSubmit = await this.processCodeSubmit();

                    if (successCodeSubmit) {
                        this.currentStep = this.steps[2];
                    } else {
                        codeField.errorText = codeField.errors.invalid_code;
                    }

                    break;
                case 2:
                    const cancelReasonField = this.formData.cancelReason;
                    cancelReasonField.errorText = '';

                    const cancelResponse = await this.submitSubscriptionCancel(phoneField.text, codeField.text, cancelReasonField.value);
                    const successCancelSubmit = this.parseSubmitResponse(cancelResponse)

                    if (successCancelSubmit) {
                        this.currentStep = this.steps[3];
                    } else {
                        cancelReasonField.errorText = cancelResponse.data || cancelReasonField.errors.cancel_error;
                    }

                    break;
                case 3:
                    this.closeModal();
                    break;
            }
        },
        async onCodeRepeatRequest() {
            const successPhoneSubmit = this.processPhoneSubmit();

            if (successPhoneSubmit) {
                this.startCodeTimeout();
            }
        },
        startCodeTimeout(intervalId) {
            this.codeRepeatTimeout = this.codeMaxTimeout;

            const lastCodeRequestTime = localStorage.getItem('code_last_request');

            if (!lastCodeRequestTime) {
                localStorage.setItem('code_last_request', Date.now());
            } else {
                const secondsDiff = millisToMinutesAndSeconds(Date.now() - lastCodeRequestTime);
                const newTimeout = this.codeMaxTimeout - secondsDiff;

                if (secondsDiff < this.codeMaxTimeout) {
                    this.codeRepeatTimeout = newTimeout;
                }
            }

            intervalId = setInterval(() => {
                if (this.codeRepeatTimeout <= 0) {
                    localStorage.removeItem('code_last_request');
                    clearInterval(intervalId);
                } else {
                    this.codeRepeatTimeout--
                }
            }, 1000)
        },
        submitPhone(phone) {
            return this.getCancelSmsCodeByPhoneNumber(phone);
        },
        submitCode(code) {
            return this.submitCancelSmsCode(code);
        },
        async processPhoneSubmit() {
            const phoneField = this.formData.phone;
            const phoneResponse = await this.submitPhone(phoneField.text);
            const successPhoneSubmit = this.parseSubmitResponse(phoneResponse);

            return successPhoneSubmit
        },
        async processCodeSubmit() {
            const codeField = this.formData.code;
            const codeResponse = await this.submitCode(codeField.text);
            const successCodeSubmit = this.parseSubmitResponse(codeResponse);

            return successCodeSubmit
        },

        parseSubmitResponse(response) {
            return response?.status === 'ok' && response?.error === false
                ? true
                : false
        },

        getCancelSmsCodeByPhoneNumber,
        submitCancelSmsCode,
        submitSubscriptionCancel

    },
    computed: {
        cancelReasons() {
            return SUBSCRIPTION_CANCEL_REASONS;
        },
        codeMaxTimeout() {
            return CODE_REPEAT_MAX_TIMEOUT;
        },
        submitIsDisabled() {
            switch (this.steps.indexOf(this.currentStep)) {
                case 0:
                    return numbersLengthFromString(this.formData.phone.text) !== PHONE_LENGTH;
                case 1:
                    return !this.formData.code.text.length;
                case 2:
                case 3:
                    return false;
            }
        },
        submitButtonText() {
            switch (this.steps.indexOf(this.currentStep)) {
                case 3:
                    return 'Закрыть окно';
                default:
                    return 'Подтвердить';
            }
        },
        modalSubtitle() {
            switch (this.steps.indexOf(this.currentStep)) {
                case 0:
                case 1:
                    return 'Укажите мобильный телефон использованный при оформлении полиса, дождитесь кода в смс сообщении и нажмите подтвердить';
                case 2:
                    return 'Нам очень жаль и мы хотим стать лучше!\nПочему вы отменяете подписку?';
                case 3:
                    return 'Ваша подписка на полис будет отменена с даты окончания действующего договора страхования. Для закрытия окна нажмите на кнопку ниже';
            }
        }
    },
    watch: {
        codeWasSent(wasSent) {
            if (wasSent) {
                this.startCodeTimeout(this.codeRepeatIntervalId)
            }
        },
    },
}
</script>

<style scoped lang="scss">
.modal-subscription-cancel {
    &__form {
        margin-top: rem($gap-big);
    }

    &__code-repeat {
        display: flex;
        align-items: center;

        margin-top: rem($gap-mini);

        &-comment {
            $safe-margin: 10px;

            @include text-small;
            word-break: keep-all;

            margin-right: rem($safe-margin);

            &--accent {
                color: $primary;
            }
        }

        &-button-send {
            margin-left: auto;
        }
    }

    &__radio {
        margin-top: rem($gap-big);

        &-group {
            margin-bottom: rem($gap-small);
        }
    }
}
</style>
